import { format } from 'date-fns';
import { FunctionComponent, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchableInputsDropdown from '../../components/common/SearchableDropdown';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { MASTER_STATUS_VALUES } from '../../api/query';
import Loader from '../../components/common/Loader';
import InputsNew from '../../components/common/InputsNew';
import { CHANGE_TASK_STATUS } from '../../api/mutation';
import { showError, showSuccess } from '../../components/common/ToastNotification';
import { useAuth } from '../../context/AuthContext';
import BreadCumb from '../../components/common/Breadcrumb';
import backArrow from "../../assets/task/back_arrow.svg";

const ClockOutTask: FunctionComponent = () => {
    //hooks
    const navigate = useNavigate();
    const location = useLocation();
    const { userInfo } = useAuth();
    const state = location.state as {
        task: any
    };

    // variables
    const { task } = state
    const employeeID = userInfo?.employee_id || '';
    const [statusOptions, setStatusOptions] = useState<{ key: string; name: string }[]>([]);
    const taskStartDate = task?.start_datetime
        ? format(task?.start_datetime, 'MM-dd-yyyy') : ""

    const taskEndDate = task?.end_datetime
        ? format(task?.end_datetime, 'MM-dd-yyyy') : ""

    const taskChecklist: {
        key: string;
        name: string;
        status: boolean;
    }[] = task?.checklist_items.map((ci: any) => ({
        key: parseInt(ci.id),
        name: ci.checklist_name,
        status: ci.status
    }));

    const defaultCheckedKeys = taskChecklist.filter(ci => ci.status).map(ci => ci.key);

    // Validation schema
    const addTaskSchema = yup.object().shape({
        durationTimeHour: yup
            .number()
            .typeError("")
            .required("")
            .min(0, "Hours should be between 0 to 23")
            .max(23, "Hours should be between 0 to 23"),
        durationTimeMinute: yup
            .number()
            .typeError("")
            .required("")
            .min(0, "Minutes should be between 0 to 59")
            .max(59, "Minutes should be between 0 to 59"),
        status: yup.string().required('This field is required'),
        statusID: yup.string().required('This field is required'),
        taskChecklist: yup.array()
            .of(yup.number())
            .when('status', {
                is: (val: string) => val?.toLowerCase() === 'closed',
                then: (schema) =>
                    schema.test(
                        'all-tasks-completed',
                        'All task Checklist must be selected when status is Closed',
                        function (value) {
                            return (
                                Array.isArray(value) &&
                                value.length === task?.checklist_items?.length
                            );
                        }
                    ),
                otherwise: (schema) => schema // no validation if not completed
            }),
        // taskChecklist: yup
        //     .array()
        //     .of(yup.string())
        //     .min(1, 'At least one task must be selected'),
        notes: yup.string()
            .test('not-only-whitespace', 'Please add a valid Note', value => !value || value.trim().length > 0)
            .required('This field is required')
    });

    // useForm setup
    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
        trigger,
        watch,
        control,
        getValues
    } = useForm<any>({
        resolver: yupResolver(addTaskSchema)
    });

    //api
    const { data, loading } = useQuery(MASTER_STATUS_VALUES, {
        fetchPolicy: 'no-cache',
    });

    const [changeTaskStatus, { loading: changeTaskLoading }] = useMutation(CHANGE_TASK_STATUS, {
        fetchPolicy: 'no-cache',
    })

    //useEffects
    useEffect(() => {
        if (data?.master_status_values?.status_code === 200) {
            const options = data.master_status_values?.status_values?.map((item: any) => ({
                key: item.id,
                name: item.name
            }));
            setStatusOptions(options);

            const existingStatus = data.master_status_values?.status_values?.find((s: any) => s.name === task?.status_name)

            setValue('status', existingStatus?.name || '');
            setValue('statusID', existingStatus?.id || '');
        }

    }, [data])

    useEffect(() => {
    if (!task) return;

    setValue(
        'durationTimeHour',
        task?.today_spent_hours != null
            ? String(task.today_spent_hours)
            : ''
    );

    setValue(
        'durationTimeMinute',
        task?.today_spent_minutes != null
            ? String(task.today_spent_minutes)
            : ''
    );


    // setValue(
    //     'notes',
    //     task?.activities[0]?.comment != null
    //         ? String(task.activities[0]?.comment)
    //         : ''
    // );

}, [task, setValue]);

    //watch
    const watchStatus = watch('status');
    const watchTaskChecklist = watch('taskChecklist');
    const durationHours = Number(watch("durationTimeHour")) || 0;
    const durationMinutes = Number(watch("durationTimeMinute")) || 0;

    useEffect(() => {
        if (durationHours > 23) {
            showError("Duration hours cannot be more than 23.");
            setValue("durationTimeHour", 0);
            return;
        }

        if (durationMinutes > 59) {
            showError("Duration minute cannot be more than 59.");
            setValue("durationTimeMinute", 0);
            return;
        }
    }, [
        durationHours,
        durationMinutes
    ]);

    function handleKeyDown(event: any): void {
        const { key } = event;
        // Allow control keys like backspace, delete, arrows, etc.
        if (
            key === "Backspace" ||
            key === "Delete" ||
            key === "ArrowLeft" ||
            key === "ArrowRight" ||
            key === "Tab" ||
            key === "Enter"
        ) {
            return;
        }

        // Prevent non-numeric keys
        if (!/^[0-9]$/.test(key)) {
            event.preventDefault();
        }
    };

    function handlePaste(event: React.ClipboardEvent<HTMLInputElement>): void {
        const paste = event.clipboardData.getData("text");
        if (!/^\d+$/.test(paste)) {
            event.preventDefault();
        }
    };

    function handleSelectStatus(key: number, name: string): void {
        setValue('statusID', key)
        setValue('status', name)
        trigger('status')
    }

    function handleSelectAll(): void {
        const allTasks = taskChecklist.map((t: any) => t.key);
        setValue('taskChecklist', allTasks);
    }

    function handleDeselectAll(): void {
        setValue('taskChecklist', []);
    };

    async function onSubmit(data: any): Promise<void> {
        const selectedIds = data?.taskChecklist?.map((id: string) => parseInt(id)) || [];

        const checklists = taskChecklist.map((item) => ({
            id: item.key, // assuming `taskChecklist` has keys as numbers
            status: selectedIds.includes(item.key),
        }));

        const changeTaskStatusInput = {
            goal_id: task?.goal_id,
            task_id: parseInt(task?.id),
            status_id: parseInt(data?.statusID),
            comment: data?.notes,
            assigned_to: task?.assigned_to_id,
            Checklists: checklists,
            time_spent_hr: data?.durationTimeHour,
            time_spent_min: data?.durationTimeMinute
        }

        try {
            const response = await changeTaskStatus({
                variables: {
                    created_by_id: parseInt(employeeID),
                    input: changeTaskStatusInput
                }
            });
            if (response.data.change_task_status.status_code === 200) {
                showSuccess(response.data.change_task_status.message)
                navigate(-1)
            }
            if (response.data.change_task_status.status_code === 400) {
                showError(response.data.change_task_status.message)
            }
        } catch (error) {
            if (error instanceof ApolloError) {
                showError(error.message)
            }
        }
    }

    return (
        <>
            <form className="w-full relative min-h-screen overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb clientName={task?.assigned_to_name} />
                    {/* <div className="self-stretch rounded flex flex-col items-start justify-start text-dimgray">
                        <div className="flex flex-row items-center justify-start">
                            <div className="overflow-hidden flex flex-row items-center justify-start gap-2">
                                <div className="relative [text-decoration:underline]">Dashboard</div>
                                <img className="w-4 relative h-4 overflow-hidden shrink-0" alt="" src="Right 16px.svg" />
                                <div className="relative font-semibold text-gray">Change Status</div>
                            </div>
                        </div>
                    </div> */}
                    <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                        <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                            onClick={() => navigate(-1)}
                        />
                        <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Change Status</h3>

                    </div>
                    <div className="self-stretch rounded-lg bg-surface-bg-primary flex flex-col items-start justify-start p-8 gap-6">
                        <div className="self-stretch rounded flex flex-col items-start justify-center gap-4">
                            <div className="self-stretch grid flex-row items-start justify-start gap-2.5">
                                <h5 className="my-0 flex-1 relative inline-block max-w-full dark:text-white whitespace-pre-wrap"><b>{task?.title}</b></h5>
                                {/* <div className="rounded-[50px] flex flex-row items-center justify-center py-1 px-4 text-darkmagenta">
                                    <div className="relative">Knowledge Base</div>
                                </div> */}
                                <div className="self-stretch flex flex-row items-start justify-start gap-2.5">
                                    <div className="rounded-[50px] bg-antiquewhite border-orange border-solid border-[1px] flex flex-row items-center justify-center py-1 px-4">
                                        <div className="relative">{task?.status_name}</div>
                                    </div>
                                    {task?.task_tags?.length > 0 && (
                                        task.task_tags.map((t: any) => {
                                            return (
                                                <div className="rounded-[100px] bg-crimson flex flex-row items-center justify-center py-1 px-2 text-sm text-white right_status_clockout">
                                                    <div className="relative font-semibold">{t?.tags_name}</div>
                                                </div>
                                            )
                                        })
                                    )}
                                    {/* <div className="flex flex-row items-center justify-center py-1 px-2 text-sm text-white ">
                                        <a href="" className="relative dark:text-white no-underline">Knowledge Base</a>
                                    </div> */}
                                </div>
                            </div>
                            <div className="self-stretch flex flex-row items-start justify-start gap-[16px]">
                                <div className="self-stretch flex flex-row items-center justify-start py-0  gap-2.5">
                                    <div className="flex flex-row items-center justify-center gap-1.5">
                                        {/* <img className="w-6 relative max-h-full overflow-hidden shrink-0 hidden" alt="" src="manager_svgrepo.com.svg" />
                                    <img className="w-[18.5px] relative h-[17.8px] hidden" alt="" src="Group 609.svg" />
                                    <img className="w-[18.5px] relative h-[17.8px]" alt="" src="Group 610.svg" /> */}
                                        <div className="relative">Schedule</div>
                                    </div>
                                    <div className="flex flex-row items-center justify-center text-dimgray">
                                        <div className="relative">{`${taskStartDate} - ${taskEndDate}`}</div>
                                    </div>
                                </div>
                                <div className="self-stretch flex flex-row items-center justify-start py-0  gap-2.5">
                                    <div className="flex flex-row items-center justify-center gap-1.5">
                                        {/* <img className="w-6 relative max-h-full overflow-hidden shrink-0 hidden" alt="" src="manager_svgrepo.com.svg" />
                                    <img className="w-[18.5px] relative h-[17.8px] hidden" alt="" src="Group 609.svg" />
                                    <img className="w-[18.5px] relative h-[17.8px]" alt="" src="Group 610.svg" /> */}
                                        <div className="relative">Last Updated</div>
                                    </div>
                                    <div className="flex flex-row items-center justify-center text-dimgray">
                                        <div className="relative">{format(task?.updated_on, 'MMM dd, yyyy, hh:mmaaa')}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="self-stretch grid grid-cols-4 mq1900:grid-cols-4 mq1425:grid-cols-4 mq1275:grid-cols-4  mq950:grid-cols-2  mq825:grid-cols-1 mq575:grid-cols-1 flex-row items-start justify-start py-0 pl-0 gap-[16px] mq950:box-border mq1425:flex-wrap mq1425:box-border  mq450:box-border">
                            <div className="self-stretch flex items-end justify-start py-0  gap-[8px] ">
                                <div className="flex flex-col items-start justify-start">
                                    <InputsNew
                                        label={"Total Time Spent*"}
                                        contentPlaceholder={"HH"}
                                        type="number"
                                        maxValue={2}
                                        onKeyDown={handleKeyDown}
                                        onPaste={handlePaste}
                                        register={{ ...register("durationTimeHour") }}
                                        errorMessage1={errors.durationTimeHour}
                                        autoComplete={"off"}
                                    />

                                </div>

                                {/* Minutes */}
                                <div className="flex flex-col items-start justify-start">
                                    <InputsNew
                                        label={""}
                                        contentPlaceholder={"MM"}
                                        type="number"
                                        maxValue={2}
                                        onKeyDown={handleKeyDown}
                                        onPaste={handlePaste}
                                        register={{ ...register("durationTimeMinute") }}
                                        errorMessage1={errors.durationTimeMinute}
                                        autoComplete={"off"}
                                    />
                                </div>
                            </div>
                            {/* <div className="w-[238.7px] h-[76px] hidden" />
                            <div className="w-[238.7px] h-[76px] hidden" /> */}
                            <SearchableInputsDropdown
                                label="Status*"
                                initialValue={watchStatus}
                                placeHolder="Select Status"
                                options={statusOptions}
                                onSelect={handleSelectStatus}
                                errorMessage={errors?.status}
                            />
                        </div>
                        <div className="grid  gap-[16px] checklist-section-dv border-surface-border-blg-dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid  border-surface-border undefined ">
                            <div className="grid self-stretch items-center gap-2 min-w-full">
                                <div className="self-stretch flex items-center justify-start py-0  gap-[8px] ">
                                    <h6 className="my-0 relative dark:text-white whitespace-pre-wrap"><b>Select Task Checklist*</b></h6>
                                    <span className="checkliste-selected-count">{watchTaskChecklist?.length}</span>
                                </div>
                                <div className=" selectall_header_bg self-stretch flex flex-row items-center justify-start pr-0  mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border gap-[8px]">
                                    <input
                                        type="checkbox"
                                        id="selectAllCheckbox"
                                        // checked={taskChecklist.length > 0 && taskChecklist.every(task => control._formValues.taskChecklist?.includes(task.key))}
                                        checked={taskChecklist.length > 0 && taskChecklist.every(task => watchTaskChecklist?.includes(task.key))}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                handleSelectAll();
                                            } else {
                                                handleDeselectAll();
                                            }
                                        }}
                                    />
                                    <label htmlFor="selectAllCheckbox" className="font-semibold">Select All</label>
                                </div>
                            </div>
                            <Controller
                                control={control}
                                name="taskChecklist"
                                // defaultValue={[]}
                                defaultValue={defaultCheckedKeys}
                                render={({ field }) => (
                                    <div className="grid grid-cols-2 mq575:grid-cols-1 gap-2">
                                        {taskChecklist?.map((task, index) => {
                                            const isChecked = field.value?.includes(task?.key);
                                            return (
                                                <div className="taskchecklist_dv">
                                                    <label
                                                        key={task?.key}
                                                        className={`py-2 border flex items-center rounded cursor-pointer ${isChecked ? 'border-purple-500 bg-white active_taskchecklist_dv' : 'border-gray-300'
                                                            }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            value={task?.key}
                                                            checked={isChecked}
                                                            onChange={() => {
                                                                const updated = isChecked
                                                                    ? field.value.filter((t: string) => t !== task?.key)
                                                                    : [...field.value, task?.key];
                                                                field.onChange(updated);
                                                            }}
                                                        // className="hidden"
                                                        />
                                                        {task?.name}
                                                    </label>
                                                </div>

                                            );
                                        })}
                                    </div>
                                )}
                            />
                            {errors.taskChecklist && (
                                <p className="text-red-600 text-sm mt-1">
                                    {typeof errors.taskChecklist.message === 'string' ? errors.taskChecklist.message : 'Invalid input'}
                                </p>
                            )}
                        </div>
                        <div className="self-stretch flex flex-col items-start justify-start text-sm">
                            <div className="self-stretch rounded-radius-md  overflow-hidden flex flex-row items-start justify-start pt-2 shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] ">
                                <InputsNew
                                    contentPlaceholder="Notes"
                                    label="Add Notes*"
                                    type="textarea"
                                    register={register('notes')}
                                    errorMessage={errors.notes}
                                />
                            </div>
                        </div>
                        <div className="self-stretch flex flex-row items-center justify-end gap-2 text-center text-darkmagenta">
                            <div className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative ">Cancel</div>
                            </div>
                            <button className="cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface  flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue text-white"
                                type='submit'
                            >
                                <div className="relative">Save</div>
                            </button>
                        </div>
                    </div>
                </div>
            </form>
            {(loading || changeTaskLoading) && (
                <Loader />
            )}
        </>
    );
};

export default ClockOutTask;
